/**
 * Circular dependency prevention:
 * - useBasket
 */
import { computed, readonly, reactive } from 'vue'
import { isRequired } from '@/utils/CustomErrors'
import { isEmpty } from 'lodash'

import { events$ } from '@/services'

import { COLLECTIVE_REQUIRED_FLAGS } from '@/config/constants'
import { EVENT_BASKET, EVENT_COLLECTIVE_LOADED } from '@/config/events'

import useBackend from '@/hooks/useBackend'
import useBasket from '@/hooks/useBasket'
import useDistribution from '@/hooks/useDistribution'
import useLogger from '@/hooks/useLogger'
import useProduct from '@/hooks/useProduct'

import config from '@/config'

// Hooks
const { setup, doGet } = useBackend()
const { createDistributionPartners } = useDistribution()
const { logInfo } = useLogger()
const { isProductReloadNecessary } = useProduct()

setup('/ovpv3/public', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    return Promise.reject(error)
  },
})

// Data
const collective = reactive({})

events$.on(EVENT_BASKET.UPDATED, async ({ basket, originalBasket }) => {
  const doReload = isProductReloadNecessary(basket, originalBasket)
  if (doReload && basket.collective?.contractNumber) {
    await install(basket, basket.collective.contractNumber)
  }
})

// Computed
const isCollective = computed(() => {
  const { basket } = useBasket()

  return !isEmpty(basket.collective)
})

// Methods
async function install(basket, contractNumber) {
  console.log('collective install called')
  const cConfig = await fetchCollectiveConfig({ contractNumber, contractStartDate: basket.contractStartDate })
  const cContent = await fetchCollectiveContent(basket.language.toLowerCase())

  const payload = Object.assign({}, cConfig, cContent[cConfig.channel], cContent[contractNumber])
  setCollective(payload)

  const distributionPartners = createDistributionPartners(payload.distributionPartnerSanitas)

  events$.emit(EVENT_COLLECTIVE_LOADED, distributionPartners)
  return { collective, distributionPartners }
}

function setCollective(payload) {
  if (collective === payload) return collective

  Object.assign(collective, payload)
  logInfo(['%cCOLLECTIVE SET', 'color: green', collective])
  return collective
}

function collectiveGroup() {
  if (!collective?.personGroups) return undefined
  const { basket } = useBasket()

  const { contractOwner, persons } = basket

  let personGroup
  if (contractOwner?.personId) {
    personGroup = persons.find(p => p.personId === contractOwner.personId).collectivePersonGroup
  } else {
    personGroup = persons[0]?.collectivePersonGroup
  }

  return collective.personGroups.find(g => g.groupNumber === personGroup)
}

async function fetchCollectiveConfig({
  contractNumber = isRequired('contractNumber'),
  contractStartDate = isRequired('contractStartDate'),
}) {
  const response = await doGet(`/collectiveconfig/${contractNumber}`, { contractStartDate })
  return response.data
}

async function fetchCollectiveContent(language) {
  const content = await import(`@/language/collective/collective.${language}.json`)
  return content.default
}

function isPersonnelNumberRequired() {
  const group = collectiveGroup()
  return COLLECTIVE_REQUIRED_FLAGS.includes(group?.personnelNumberDescriptor?.state)
}

export default function useCollective() {
  return {
    collective: readonly(collective),
    isCollective,

    install,

    fetchCollectiveConfig,
    fetchCollectiveContent,
    isPersonnelNumberRequired,
    collectiveGroup,
    setCollective,
  }
}
